import { KeychainAccess, SecureStorage } from "@aparajita/capacitor-secure-storage";
import { Capacitor } from "@capacitor/core";

const SESSION_KEY = "operator-session";
const PENDING_REVOCATION_KEY = "operator-session-pending-revocation";
const SESSION_KEY_PREFIX = "billiard-erp-";
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

export type NativeSessionMode = "PERSONAL" | "SHARED_SHIFT";

export interface NativeOperatorSession {
  accessToken: string;
  csrfToken: string;
  expiresAt: string;
  mode: NativeSessionMode;
  shiftId: string | null;
}

export interface NativePendingSessionRevocation {
  accessToken: string;
  csrfToken: string;
  expiresAt: string;
  appVersion: string;
}

export interface NativeSecureStorage {
  setKeyPrefix(prefix: string): Promise<void>;
  setSynchronize(sync: boolean): Promise<void>;
  setDefaultKeychainAccess(access: KeychainAccess): Promise<void>;
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<boolean>;
}

function normalizedSession(value: unknown, now = Date.now()): NativeOperatorSession | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.accessToken !== "string" ||
    typeof candidate.csrfToken !== "string" ||
    typeof candidate.expiresAt !== "string" ||
    !TOKEN_PATTERN.test(candidate.accessToken) ||
    !TOKEN_PATTERN.test(candidate.csrfToken) ||
    !Number.isFinite(Date.parse(candidate.expiresAt)) ||
    Date.parse(candidate.expiresAt) <= now
  ) {
    return null;
  }
  if (
    candidate.mode !== undefined &&
    candidate.mode !== "PERSONAL" &&
    candidate.mode !== "SHARED_SHIFT"
  ) {
    return null;
  }
  // Sessions written before device modes existed were private-device sessions.
  // Preserve that one safe migration path while rejecting unknown future values.
  const mode = candidate.mode === "SHARED_SHIFT" ? "SHARED_SHIFT" : "PERSONAL";
  const shiftId =
    mode === "SHARED_SHIFT" && typeof candidate.shiftId === "string" && candidate.shiftId.length > 0
      ? candidate.shiftId
      : null;
  // Deliberately copy only the revocable session fields. A password or any other
  // login-form value can never cross this storage boundary.
  return {
    accessToken: candidate.accessToken,
    csrfToken: candidate.csrfToken,
    expiresAt: candidate.expiresAt,
    mode,
    shiftId,
  };
}

function normalizedPendingRevocation(
  value: unknown,
  now = Date.now(),
): NativePendingSessionRevocation | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.accessToken !== "string" ||
    typeof candidate.csrfToken !== "string" ||
    typeof candidate.expiresAt !== "string" ||
    typeof candidate.appVersion !== "string" ||
    !TOKEN_PATTERN.test(candidate.accessToken) ||
    !TOKEN_PATTERN.test(candidate.csrfToken) ||
    !Number.isFinite(Date.parse(candidate.expiresAt)) ||
    Date.parse(candidate.expiresAt) <= now ||
    !VERSION_PATTERN.test(candidate.appVersion)
  ) {
    return null;
  }
  return {
    accessToken: candidate.accessToken,
    csrfToken: candidate.csrfToken,
    expiresAt: candidate.expiresAt,
    appVersion: candidate.appVersion,
  };
}

export function createNativeSessionVault(
  storage: NativeSecureStorage,
  nativePlatform: boolean,
  now: () => number = Date.now,
) {
  let current: NativeOperatorSession | null = null;
  let pendingRevocation: NativePendingSessionRevocation | null = null;
  let storageMutationQueue: Promise<void> = Promise.resolve();
  let operationGeneration = 0;
  let configured: Promise<void> | null = null;
  const configure = (): Promise<void> => {
    if (!configured) {
      configured = Promise.all([
        storage.setKeyPrefix(SESSION_KEY_PREFIX),
        storage.setSynchronize(false),
        storage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly),
      ])
        .then(() => undefined)
        .catch((error: unknown) => {
          // Native security services can be temporarily unavailable while the
          // device is unlocking or resuming. Do not cache a rejected promise:
          // a later explicit login or runtime retry must be able to re-check.
          configured = null;
          throw error;
        });
    }
    return configured;
  };
  const loadPendingRevocation = async (
    expectedGeneration: number,
  ): Promise<NativePendingSessionRevocation | null> => {
    await configure();
    const stored = await storage.get(PENDING_REVOCATION_KEY);
    if (expectedGeneration !== operationGeneration) return null;
    if (stored === null || stored === undefined) {
      pendingRevocation = null;
      return null;
    }
    pendingRevocation = normalizedPendingRevocation(stored, now());
    if (!pendingRevocation) {
      // A malformed quarantine record cannot safely identify its server
      // session. Never promote a possibly related active token after restart.
      await storage.remove(SESSION_KEY);
      await storage.remove(PENDING_REVOCATION_KEY);
    }
    return pendingRevocation;
  };
  const mutate = <T>(operation: () => Promise<T>): Promise<T> => {
    const result = storageMutationQueue.then(operation);
    storageMutationQueue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  };

  return {
    isNative: nativePlatform,
    current(): NativeOperatorSession | null {
      return current;
    },
    pendingRevocation(): NativePendingSessionRevocation | null {
      return pendingRevocation;
    },
    async restorePendingRevocation(): Promise<NativePendingSessionRevocation | null> {
      if (!nativePlatform) return null;
      const expectedGeneration = operationGeneration;
      return mutate(() => loadPendingRevocation(expectedGeneration));
    },
    async restore(): Promise<NativeOperatorSession | null> {
      if (!nativePlatform) return null;
      const expectedGeneration = operationGeneration;
      return mutate(async () => {
        if (expectedGeneration !== operationGeneration) return null;
        current = null;
        if (await loadPendingRevocation(expectedGeneration)) return null;
        if (expectedGeneration !== operationGeneration) return null;
        const restored = normalizedSession(await storage.get(SESSION_KEY), now());
        if (expectedGeneration !== operationGeneration) return null;
        // A public cashier device session is deliberately memory-only. Even if an
        // older build wrote one, an App restart must fail closed to the login page.
        current = restored?.mode === "PERSONAL" ? restored : null;
        if (!current) await storage.remove(SESSION_KEY);
        return current;
      });
    },
    async savePendingRevocation(value: unknown): Promise<NativePendingSessionRevocation> {
      if (!nativePlatform) throw new Error("待撤销会话只能在 Android/iOS App 中保存");
      const normalized = normalizedPendingRevocation(value, now());
      if (!normalized) throw new Error("待撤销会话标记无效或已过期");
      const expectedGeneration = operationGeneration;
      return mutate(async () => {
        await configure();
        await storage.set(PENDING_REVOCATION_KEY, normalized);
        if (expectedGeneration !== operationGeneration) {
          throw new Error("secure storage operation was invalidated");
        }
        pendingRevocation = normalized;
        return normalized;
      });
    },
    async save(value: unknown): Promise<NativeOperatorSession> {
      if (!nativePlatform) throw new Error("原生安全会话只能在 Android/iOS App 中保存");
      const session = normalizedSession(value, now());
      if (!session) throw new Error("原生安全会话无效或已过期");
      const expectedGeneration = operationGeneration;
      return mutate(async () => {
        await configure();
        if (session.mode === "PERSONAL") {
          await storage.set(SESSION_KEY, session);
        } else {
          await storage.remove(SESSION_KEY);
        }
        if (expectedGeneration !== operationGeneration) {
          throw new Error("secure storage operation was invalidated");
        }
        current = session;
        return session;
      });
    },
    async clearPendingRevocation(): Promise<void> {
      if (!nativePlatform) {
        pendingRevocation = null;
        return;
      }
      const expectedGeneration = operationGeneration;
      return mutate(async () => {
        await configure();
        await storage.remove(PENDING_REVOCATION_KEY);
        if (expectedGeneration !== operationGeneration) return;
        pendingRevocation = null;
      });
    },
    async clearIfCurrent(accessToken: string): Promise<void> {
      if (!nativePlatform || current?.accessToken !== accessToken) return;
      const expectedGeneration = operationGeneration;
      return mutate(async () => {
        if (expectedGeneration !== operationGeneration) return;
        if (current?.accessToken !== accessToken) return;
        current = null;
        await configure();
        await storage.remove(SESSION_KEY);
      });
    },
    async clear(): Promise<void> {
      current = null;
      if (!nativePlatform) return;
      const expectedGeneration = operationGeneration;
      return mutate(async () => {
        if (expectedGeneration !== operationGeneration) return;
        current = null;
        await configure();
        await storage.remove(SESSION_KEY);
      });
    },
    /** Fail closed without waiting for a native bridge callback that may never arrive. */
    invalidatePendingOperations(): void {
      operationGeneration += 1;
      current = null;
      pendingRevocation = null;
    },
    /** @internal Clears process memory only; persistent storage is untouched. */
    resetProcessMemoryForTests(): void {
      if (import.meta.env.MODE !== "test") {
        throw new Error("native session process reset is test-only");
      }
      current = null;
      pendingRevocation = null;
      storageMutationQueue = Promise.resolve();
      operationGeneration = 0;
    },
  };
}

export const nativeSessionVault = createNativeSessionVault(
  SecureStorage as NativeSecureStorage,
  Capacitor.isNativePlatform(),
);

export function isNativeOperatorApp(): boolean {
  return nativeSessionVault.isNative;
}
